'use client';

import { createRevealObserver } from '@/lib/reveal-observer';
import React, { useEffect, useRef } from 'react';

const stages = [
  {
    number: '01',
    title: 'Understand',
    description:
      'We start by understanding the company. The market. The product. The team. The traction. The capital requirement. The long-term ambition.',
  },
  {
    number: '02',
    title: 'Position',
    description:
      'We identify the strongest investment narrative. What makes the opportunity compelling? What evidence supports the thesis? Where are the gaps?',
  },
  {
    number: '03',
    title: 'Target',
    description:
      'We identify the investor archetypes most relevant to the company. Not simply: who has money? But: who has a reason to invest?',
  },
  {
    number: '04',
    title: 'Connect',
    description:
      'We facilitate introductions to relevant investors within our network and syndicate ecosystem.',
  },
  {
    number: '05',
    title: 'Advance',
    description:
      'Investor conversations are followed through the fundraising process with structured follow-up, feedback and relationship management.',
  },
];

export default function ApproachSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = createRevealObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.scroll-fade').forEach((el) => {
              el.classList.add('visible');
            });
          }
        });
      },
      { rootMargin: '0px 0px -60px 0px', threshold: 0.05 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="approach" className="py-24 px-6 border-t border-primary/10">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-end mb-20">
          <div>
            <p className="section-label scroll-fade mb-5">The Aregn Approach</p>
            <h2 className="text-section-title font-sans font-semibold text-foreground scroll-fade scroll-fade-delay-1">
              Capital raising is a process,{' '}
              <span className="font-serif italic text-primary">not a pitch.</span>
            </h2>
          </div>
          <p className="text-sm text-muted-foreground font-light leading-relaxed scroll-fade scroll-fade-delay-2 lg:pb-2">
            Our approach is built around five stages — each designed to move the company closer to
            the right investor conversations, not just more conversations.
          </p>
        </div>

        {/* Stages — horizontal flow on desktop, vertical on mobile */}
        <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {stages?.map((stage, i) => (
            <div
              key={stage?.number}
              className={`bento-card rounded-2xl p-6 flex flex-col scroll-fade`}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              {/* Number + connector */}
              <div className="flex items-center gap-3 mb-5">
                <span className="w-8 h-8 rounded-full border border-primary/40 flex items-center justify-center text-xs font-semibold text-primary shrink-0">
                  {stage?.number}
                </span>
                {i < stages?.length - 1 && (
                  <div className="hidden lg:block flex-1 h-px bg-gradient-to-r from-primary/30 to-transparent" />
                )}
              </div>
              <h3 className="text-base font-semibold text-foreground mb-3">{stage?.title}</h3>
              <p className="text-xs text-muted-foreground font-light leading-relaxed flex-1">
                {stage?.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
